import { useContext, useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { payForOrder } from 'services/PaymentService';
import {
  mergeValidationSchemas,
  personalDetailsFormSchema,
  termsAndConditionsSchema,
  cardPaymentFormSchema,
} from 'utils/form-validation-schema';
import { PaymentGiftCertificate } from 'components/Payment/PaymentGiftCertificate/PaymentGiftCertificate';
import { PaymentCardPayment } from 'components/Payment/PaymentCardPayment/PaymentCardPayment';
import { PaymentPersonalDetails } from 'components/Payment/PaymentPersonalDetails/PaymentPersonalDetails';
import { PaymentDonation } from 'components/Payment/PaymentDonation/PaymentDonation';
import { PaymentPageContext } from 'components/Payment/PaymentPageProvider';

import './PaymentForm.scss';

export const PaymentForm = (props) => {
  const { PersonalInformation, currentCartSnapShot } = props;
  const { order, refreshOrderData } = useContext(PaymentPageContext);
  const [paymentIsProcessing, setPaymentIsProcessing] = useState(false);
  const [apiResponseErrorMessage, setApiResponseErrorMessage] = useState('');
  const [subscribeCheckboxSelected, setSubscribeCheckboxSelected] = useState(true);
  const [subscribeEmailCheckboxSelected, setSubscribeEmailCheckboxSelected] = useState(true);
  const [subscribeAsiaCheckboxSelected, setSubscribeAsiaCheckboxSelected] = useState(false);
  const [pageIsRedirecting, setPageIsRedirecting] = useState(false);
  const isDonationOnly = order.Cart.TotalItems === order.Cart.DonationItems.length;
  const cartTotalIsZero = order.Cart.OrderTotal === 0;

  /*
   * Merge the validation schemas. But if the cart total is zero, then the card payment form is hidden.
   * So in that case, we don't need to add the 'cardPaymentFormSchema' schema for validation
   */
  const mergedSchemas = mergeValidationSchemas(
    personalDetailsFormSchema,
    termsAndConditionsSchema,
    !cartTotalIsZero && cardPaymentFormSchema
  );

  const methods = useForm({
    criteriaMode: 'all',
    shouldFocusError: true,
    mode: 'onChange',
    resolver: yupResolver(mergedSchemas),
  });

  const validationErrorMessage = 'Please complete the payment details and check for any errors.';
  const formIsSubmitted = methods.formState.isSubmitted;
  const formIsValid = methods.formState.isValid;
  const formHasValidationError =
    formIsSubmitted && !formIsValid && !pageIsRedirecting && !apiResponseErrorMessage;
  const formErrorMessage = formHasValidationError
    ? validationErrorMessage
    : apiResponseErrorMessage;

  const subscribeCheckboxHandler = () => {
    setSubscribeCheckboxSelected((prevState) => !prevState);
  };

  const subscribeEmailCheckboxHandler = () => {
    setSubscribeEmailCheckboxSelected((prevState) => !prevState);
  };

  const subscribeAsiaCheckboxHandler = () => {
    setSubscribeAsiaCheckboxSelected((prevState) => !prevState);
  };

  const resetAllFields = () => {
    const { resetField, trigger } = methods;
    resetField('inputCardName');
    resetField('inputCardNo');
    resetField('inputCardDate');
    resetField('inputCardSecurity');
    trigger();
  };

  const onSubmitHandler = async (paymentFormData) => {
    setPaymentIsProcessing(true);
    setApiResponseErrorMessage(false);

    const payloadData = {
      ...paymentFormData,
      giftCertificateNumber: order.Cart.RedeemedGiftCertificates[0]?.GiftCertificateNumber || '',
      subscribeCheckboxSelected,
      subscribeAsiaCheckboxSelected,
      subscribeEmailCheckboxSelected,
      currentCartSnapShot
    };

    try {
      trackEvent('add_payment_info', payloadData);
      const response = await payForOrder(payloadData);

      if (response.Redirect) {
        setPageIsRedirecting(true);
        window.location.href = response.RedirectUrl;
      } else {
        setApiResponseErrorMessage(response.PaymentDetails.ErrorMessage);
        setPaymentIsProcessing(false);
        resetAllFields();
      }
    } catch (e) {
      console.error(e);
      setPaymentIsProcessing(false);
      setApiResponseErrorMessage(defaultErrorMessage);
      resetAllFields();
    }
  };

  useEffect(() => {
    const { setValue } = methods;
    if (PersonalInformation.PhoneNumber) {
      setValue('inputPhone', PersonalInformation.PhoneNumber);
    }
    if (PersonalInformation.PostCode) {
      setValue('inputPostcode', PersonalInformation.PostCode);
    }
  }, [PersonalInformation]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmitHandler)} className="payment-page__form">
        <PaymentDonation {...props} order={order} refreshOrderData={refreshOrderData} />
        <PaymentPersonalDetails
          {...props}
          formState={methods.formState}
          subscribeCheckboxHandler={subscribeCheckboxHandler}
          subscribeCheckboxChecked={subscribeCheckboxSelected}
          subscribeAsiaCheckboxHandler={subscribeAsiaCheckboxHandler}
          subscribeEmailCheckboxHandler={subscribeEmailCheckboxHandler}
          subscribeEmailCheckboxChecked={subscribeEmailCheckboxSelected}
        />
        <div className="cart-payment">
          <div className="container">
            <h2 className="h4">Payment</h2>
            <PaymentGiftCertificate
              {...props}
              isDonationOnly={isDonationOnly}
              giftCardsInCart={order.Cart.RedeemedGiftCertificates}
            />
            <PaymentCardPayment
              {...props}
              order={order}
              refreshOrderData={refreshOrderData}
              formState={methods.formState}
              paymentIsProcessing={paymentIsProcessing}
              formErrorMessage={formErrorMessage}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

PaymentForm.propTypes = {
  PersonalInformation: PropTypes.object,
};
